import styled from '@emotion/styled';
import {
  Badge,
  Button,
  Table,
  TableProps,
  message,
  TableColumnsType,
  Tooltip,
  Dropdown,
  Menu,
  Progress,
} from 'antd';
import dayjs from 'lib/dayjs';
import { useTranslate } from '@tolgee/react';
import StatusButton from 'components/StatusButton';
import { getChunks, What, Template } from 'views/Marketing/components/PolicyTable';
import EditorPreviewer from 'components/EditorPreviewer';
import { isValidContent } from 'services/api/oms';
import { TaskQueryValue } from './Task';
import { exportTaskDetail, startTask, stopTask } from './api';
import { DownOutlined } from '@ant-design/icons';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import { useGetPermission } from 'hooks/business/useNewPermisson';

const ButtonWrapper = styled.div`
  > button:first-of-type {
    padding-left: 0;
  }
`;

type RT = Aide.Task;

export interface TaskTableProps extends TableProps<RT> {
  scenarioType: Marketing.StockoutScenarioType;
  platform: General.PlatformUpperCase;
  remainCount?: number;
  onUpdated: () => void;
  onNavTask: (query: TaskQueryValue) => void;
  cannotDispatch?: () => void;
}

function TaskTable(props: TaskTableProps) {
  const {
    scenarioType,
    platform,
    remainCount = Infinity,
    onUpdated,
    onNavTask,
    cannotDispatch,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const lang = useSystemLanguageValue();
  const { run: onDownload } = useExportFile();

  const getPermission = useGetPermission();

  const columns: TableColumnsType<RT> = [
    {
      key: 'taskName',
      dataIndex: 'taskName',
      title: t('aide.任务名称'),
      width: 180,
      fixed: 'left',
    },
    {
      key: 'regionExecuteContexts',
      dataIndex: 'regionExecuteContexts',
      title: t('aide.发送内容'),
      width: 280,
      render: (contexts: RT['regionExecuteContexts']) => {
        const [firstContext] = contexts;

        if (!firstContext) {
          return '-';
        }

        const chunks = getChunks(firstContext.executeContext);

        if (chunks.length === 0) {
          return '-';
        }

        const isTextOnly = chunks.length === 1 && chunks[0].type === 'text';

        if (!isTextOnly) {
          const chunkTypeLabelMap = {
            text: t('phrase.文本'),
            image: t('phrase.图片'),
            video: t('phrase.视频'),
            sticker: t('messageType.STICKER'),
            follow: t('phrase.邀请关注'),
            qrcode: '自动发货二维码',
          };

          return chunks.map(({ type }) => `[${chunkTypeLabelMap[type]}]`).join('');
        }

        const [{ content, translatedContent = [] }] = chunks;

        return (
          <Template>
            <EditorPreviewer
              value={isValidContent(translatedContent) ? translatedContent : content}
              hoverPop
            />
          </Template>
        );
      },
    },
    {
      key: 'regions',
      dataIndex: 'regionExecuteContexts',
      title: t('aide.站点'),
      width: 180,
      render: (contexts: RT['regionExecuteContexts']) =>
        contexts.map(({ region }) => region).join(' | '),
    },
    {
      key: 'status',
      dataIndex: 'status',
      title: t('aide.状态'),
      width: 150,
      render: (status: RT['status'], { dispatched, parseResult: { planned } }) => {
        const content = {
          PENDING: <Badge color="#3370ff" text={t('aide.未开始')} />,
          ONGOING: <Badge color="var(--fett-scenario-success-text)" text={t('aide.进行中')} />,
          FINISHED: (
            <Badge color="var(--fett-text-quaternary)" text={t('domain.task.status.completed')} />
          ),
        }[status];
        if (status === 'ONGOING') {
          return (
            <div className="flex flex-col">
              {content}
              <div className="pl-4">
                <Progress percent={Math.floor((dispatched / planned) * 100)} showInfo={false} />
              </div>
            </div>
          );
        }
        return content;
      },
    },
    {
      key: 'dispatched',
      dataIndex: 'dispatched',
      title: (
        <div>
          {t('aide.发送次数')}
          <Tooltip title={t('aide.发送次数TIP')}>
            <What />
          </Tooltip>
        </div>
      ),
      width: 150,
      render: (_, { dispatched, parseResult: { planned } }) => `${dispatched}/${planned}`,
    },
    {
      key: 'failedCount',
      dataIndex: 'failedCount',
      title: t('failures_count'),
      width: 100,
    },
    {
      key: 'creatorName',
      dataIndex: 'creatorName',
      title: t('creator'),
      width: 120,
    },
    {
      key: 'updateAt',
      dataIndex: 'updateAt',
      title: t('aide.更新时间'),
      width: 180,
      render: (updateAt: RT['updateAt']) => dayjs(updateAt).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      key: 'operation',
      title: t('操作'),
      width: 280,
      fixed: 'right',
      render: (_, record) => {
        const {
          status,
          taskId: id,
          parseResult: { planned },
          dispatched,
          regionExecuteContexts,
        } = record;

        const undispatchedCount = planned - dispatched;

        const hasPermission = regionExecuteContexts.every((ec) => {
          return getPermission('配置缺货提醒任务', { region: ec.region });
        });

        return (
          <ButtonWrapper>
            {status === 'PENDING' ? (
              <Button type="link" onClick={() => onNavTask({ platform, action: 'update', id })}>
                {t('aide.编辑')}
              </Button>
            ) : (
              <Button type="link" onClick={() => onNavTask({ platform, action: 'view', id })}>
                {t('aide.查看')}
              </Button>
            )}
            <Button
              type="link"
              onClick={() => {
                if (cannotDispatch) {
                  cannotDispatch();
                } else {
                  onNavTask({ platform, action: 'copy', id });
                }
              }}
            >
              {t('aide.复制')}
            </Button>
            {status === 'PENDING' && hasPermission && (
              <StatusButton
                type="link"
                popConfirmProps={
                  remainCount < undispatchedCount && !cannotDispatch
                    ? {
                        title: (
                          <>
                            {t('aide.发送次数剩余x次，本次需使用y次', {
                              x: remainCount,
                              y: undispatchedCount,
                            })}
                            <br />
                            {t('aide.次数使用完后，将结束任务停止发送')}
                          </>
                        ),
                      }
                    : undefined
                }
                onClick={() => {
                  if (cannotDispatch) {
                    cannotDispatch();

                    return Promise.resolve();
                  }

                  return startTask(id)
                    .then(onUpdated)
                    .catch(() => {
                      message.error(t('operation.saveFailed'));
                    });
                }}
              >
                {t('aide.启动任务')}
              </StatusButton>
            )}
            {status === 'ONGOING' && hasPermission && (
              <StatusButton
                type="link"
                popConfirmProps={{
                  title: (
                    <>
                      {t('aide.您确定要结束任务吗？')}
                      <br />
                      {scenarioType === 'OUT_STOCK_REMINDER' &&
                        t('aide.结束后将停止未发送的缺货提醒')}
                      {scenarioType === 'ORDER_FOLLOWUP' && t('结束后将停止未发送的跟单消息')}
                    </>
                  ),
                }}
                onClick={() => {
                  return stopTask(id)
                    .then(onUpdated)
                    .catch(() => {
                      message.error(t('operation.saveFailed'));
                    });
                }}
              >
                {t('aide.结束任务')}
              </StatusButton>
            )}
            {status === 'FINISHED' && (
              <Dropdown
                overlay={
                  <Menu
                    onClick={({ key }) => {
                      onDownload(() =>
                        exportTaskDetail({
                          taskId: id,
                          lang,
                          status: key as any,
                        }),
                      );
                    }}
                  >
                    <Menu.Item key="ALL">{t('download_full_details')}</Menu.Item>
                    <Menu.Item key="FAIL">{t('download_failure_details')}</Menu.Item>
                  </Menu>
                }
              >
                <Button type="link">
                  {t('download_dispatch_details')}
                  <DownOutlined />
                </Button>
              </Dropdown>
            )}
          </ButtonWrapper>
        );
      },
    },
  ];

  return <Table rowKey="taskId" columns={columns} scroll={{ x: '100%' }} {...otherProps} />;
}

export default TaskTable;
